/** @deprecated Superseded by AccountType + ProviderRole below. Kept temporarily
 * so files not yet migrated still compile; do not use in new code. Remove once
 * every file listed in the UserRole migration grep has been converted. */
export enum UserRole {

  CLIENT   = 'CLIENT',
  PHYSIO   = 'PHYSIO',
  ADMIN    = 'ADMIN',
  DOCTOR   = 'DOCTOR',
  CLINIC   = 'CLINIC',
  EDUCATOR = 'EDUCATOR',
  LEARNER  = 'LEARNER',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum BookingStatus {
  PENDING = 'PENDING',
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  CONFIRMED = 'CONFIRMED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export enum ServiceCaseStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  DISPUTED = 'disputed'
}

export interface User {
  id: string;
  uid: string;
  /** @deprecated use accountType (ADMIN/CLIENT/PROVIDER) + roles (ProviderRole[]) instead */
  role: UserRole;
  accountType: AccountType;
  accountStatus?: 'GUEST' | 'REGISTERED';
  /** Primary role string from backend: 'ADMIN' | 'CLIENT' | a ProviderRole value */
  primaryRole: AccountType | ProviderRole;
  /** Full cadre list -- empty for CLIENT/ADMIN accounts */
  roles: ProviderRole[];
  /** True for CLIENT accounts that are also enrolled as a Learner (CPD courses).
   * Backend has no LEARNER account type/cadre yet -- this flag is the interim
   * representation until one exists. See AuthPage.tsx registration flow. */
  isLearner?: boolean;
  name: string;


  phone: string;
  email: string;
  avatar?: string;
  isActive?: boolean;
  kycStatus?: 'PENDING' | 'VERIFIED' | 'REJECTED';
  publicKey?: string; // E2EE Public Key
  consentFlags?: {
    location: boolean;
    dataProcessing: boolean;
    aiRecommendations: boolean;
    marketing: boolean;
  };
  acceptedTermsVersion?: string;
  nationalIdNumber?: string;   // PII Ã¢â‚¬â€ read restricted per security spec
  nationalIdUrl?: string;
  /** GPS coords saved after user grants location permission */
  lastLocation?: {
    latitude: number;
    longitude: number;
  };
  /** Soft-delete flag Ã¢â‚¬â€ set by adminDeleteUser, never hard-deleted */
  deleted?: boolean;
  createdAt?: any;
  /** Reason shown to the user when their account is deactivated */
  deactivationReason?: string;
  deactivatedAt?: any;
  deactivationType?: 'admin' | 'auto_onboarding' | 'auto_dormancy' | 'user_suspended';
  /** User chose to keep account suspended rather than request reinstatement */
  accountSuspendedByUser?: boolean;
  /** Set true once the mandatory post-approval workforce intake form is completed. */
  workforceIntakeCompleted?: boolean;
  /** Grace-period deadline for existing providers before the intake gate becomes a hard block. */
  workforceIntakeDeadline?: string;
}

export interface PhysioProfile {
  homeVisit?: boolean;
  institutionWork?: boolean;
  institutionRatePercent?: number;
  institutionMinPatients?: number;
  serviceLines?: string;
  id: string;
  userId: string;
  businessName?: string;
  fullName: string;
  workingHours?: string; // JSON: {"mon":{"start":"08:00","end":"17:00","closed":false}, ...}
  title?: string;
  bio?: string;
  qualifications?: string[];
  services?: string[];
  /**
   * Live Firestore field name is 'map', not 'location'.
   * Note: lat/lng should be stored as numbers; string values in existing
   * documents are a data-entry error (e.g. "-o.3031" vs "-0.3031").
   */
  map?: {
    address: string;
    lat: number;
    lng: number;
    county: string;
  };
  /**
   * Alias for map Ã¢â‚¬â€ used throughout the frontend.
   * Both fields refer to the same logical data; prefer `map` for new Firestore writes.
   */
  location?: {
    address: string;
    lat: number;
    lng: number;
    county: string;
  };
  clinicLocation?: any;   // Firestore GeoPoint
  /**
   * Dual-format field:
   *  - Legacy documents stored a plain address string (e.g. "Westlands, Nairobi")
   *  - New documents store a coordinate object after clinicLocation was merged here
   * Always narrow with `typeof clinicAddress === 'object'` before accessing lat/lng.
   */
  clinicAddress?: string | { latitude: number; longitude: number };
  /** Friendly city/town label shown on cards (e.g. "Westlands, Nairobi") */
  locationCity?: string;
  /** Display name for the clinic used on booking cards */
  clinicName?: string;
  profilePhoto?: string;
  coverPhoto?: string;
  /** Whether the physio consents to their photo appearing on the public map */
  photoPublicConsent?: boolean;
  yearsExperience?: number;
  licenseNumber: string;
  pckStatus?: 'VALID' | 'EXPIRED' | 'UNVERIFIED';
  lastPckCheck?: any;
  contactPhone?: string;
  contactWhatsApp?: string;
  isActive: boolean;
  foundingMember?: boolean;
  expiryDate?: any;
  // Canonical rating fields (averageRating + reviewCount)
  averageRating?: number;
  reviewCount?: number;
  pricePerSession?: number;

  // Professional Credentials
  registrationNumber?: string;
  /** PII, admin-only - never sent on the public GET /api/profiles endpoint */
  idOrPassportNumber?: string;
  licenseSerialNumber?: string;
  licenseDocumentUrl?: string;
  nationalIdUrl?: string;
  lastActivePracticeYear?: number;
  verificationStatus?: 'PENDING' | 'SUBMITTED' | 'VERIFIED' | 'REJECTED' | 'NOT_SUBMITTED';
  credentialsSubmittedAt?: any;
  credentialsVerifiedAt?: any;
  credentialsVerifiedBy?: string;
  rejectionReason?: string;
  verificationNote?: string;
  editRequestPending?: boolean;
  editRequestReason?: string;

  /** Specialisations list (British-English spelling used throughout the app) */
  specialisations?: string[];

  acceptedConductCode?: boolean;

  /**
   * Runtime-only field injected by MapboxContainer after geocoding.
   * Never persisted to Firestore.
   */
  _resolvedLoc?: {
    latitude: number;
    longitude: number;
    isFallback: boolean;
  };

  createdAt: any;
  updatedAt?: any;
}

/**
 * Legacy collection: 'physiotherapists'
 * Superseded by PhysioProfile / physio_profiles. Read-only in production.
 * Uses snake_case and a GeoPoint location Ã¢â‚¬â€ distinct from PhysioProfile.map.
 */
export interface LegacyPhysiotherapist {
  id: string;
  name: string;
  email?: string;
  photo_url?: string;
  specialty?: string;
  rating?: number;
  location?: any;         // Firestore GeoPoint
  availability?: any;     // map of time slots
  distancefromuser?: number;
  userid?: string;        // reference string to users collection
  verified?: boolean;
}

export interface Review {
  id: string;
  physioId: string;
  clientId: string;
  clientName: string;
  rating: number;
  comment: string;
  tags?: string[];
  createdAt: any;
  physioResponse?: string;
  physioResponseAt?: any;
}

export interface Booking {
  id: string;
  clientId: string;
  clientName: string;
  clientPhone: string;
  physioId: string;
  physioName: string;
  date: string;
  timeSlot?: string;
  service?: string;
  clientPhoto?: string;
  physioPhoto?: string;
  status: BookingStatus;
  message?: string;
  physioNote?: string;
  cancellationReason?: string;
  cancelledBy?: string;
  service_case_id?: string;
  isEncrypted?: boolean;
  encMessage?: { ciphertext: string; iv: string; encryptedKeys: Record<string, string> };
  encNote?: { ciphertext: string; iv: string; encryptedKeys: Record<string, string> };
  createdAt: any;
  guestBooking?: boolean;
  appointmentType?: 'HOME_VISIT' | 'IN_CLINIC' | 'REMOTE';
  institutionBooking?: boolean;
  patientLoad?: number;
  ratePercent?: number;
  estimatedEarnings?: number;
  providerPhone?: string;
}

export interface PaymentSubmission {
  id: string;
  physioId: string;
  physioName: string;
  amount: number;
  mpesaCodeOrReference: string;
  proofImageUrl: string;
  status: PaymentStatus;
  submittedAt: any;
}

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  date: string;
  author: string;
  content: string;
}

export interface AuditLog {
  id: string;
  timestamp: any;
  actorId: string;
  action: string;
  resourceId: string;
  metadata?: any;
}

export interface Message {
  id: string;
  senderId: string;
  senderName?: string;
  receiverId: string;
  text: string;
  timestamp: number;
  read: boolean;
  service_case_id?: string;
  isEncrypted?: boolean;
  ciphertext?: string;
  iv?: string;
  encryptedKeys?: Record<string, string>;
  /** Structured encrypted payload Ã¢â‚¬â€ mirrors Booking.encMessage for components that handle both */
  encMessage?: { ciphertext: string; iv: string; encryptedKeys: Record<string, string> };
  /** Admin dashboard messages Ã¢â‚¬â€ surfaced first in user dashboards */
  fromAdmin?: boolean;
  isPinned?: boolean;
  priority?: 'high' | 'normal';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: number;
}

/** AI chatbot session log Ã¢â‚¬â€ collection: ailogs */
export interface AiLog {
  id: string;
  userId: string;
  sessionId?: string;
  createdAt: any;
  summary?: string;
}

/** AI chatbot turn Ã¢â‚¬â€ collection: chatMessages */
export interface AiChatMessage {
  id: string;
  userId: string;
  role: 'user' | 'model';
  text: string;
  timestamp: number;
  sessionId?: string;
}

/** AI or peer chat session Ã¢â‚¬â€ collection: chats */
export interface Chat {
  id: string;
  userId: string;
  participantIds?: string[];
  lastMessage?: string;
  lastMessageAt?: any;
  createdAt: any;
}

export interface HelpTicket {
  id: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
  subject: string;
  message: string;
  status: 'OPEN' | 'RESOLVED';
  adminReply?: string;
  repliedAt?: any;
  createdAt: any;
  resolvedAt?: any;
}

export interface ServiceCase {
  id: string;
  clientId: string;
  physioId: string;
  bookingId?: string;
  supportTicketId?: string;
  status: ServiceCaseStatus;
  events: {
    type:
      | 'case_created'
      | 'case_linked_to_booking'
      | 'message_detected'
      | 'support_intervened'
      | 'case_flagged_dispute'
      | 'case_closed';
    timestamp: any;
    metadata?: any;
  }[];
  createdAt: any;
  updatedAt: any;
}

export interface PhysioNeed {
  id: string;
  clientId: string;
  clientName: string;
  area: string;
  lat: number;
  lng: number;
  status: 'OPEN' | 'FULFILLED' | 'EXPIRED';
  createdAt: any;
}

/* ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬
 * APPEND THIS BLOCK TO types.ts.
 * These mirror the Java backend's enums/AccountType.java and
 * enums/UserRole.java (provider subtype) exactly. Added alongside the
 * existing UserRole enum rather than replacing it ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â other pages
 * (ClientDashboard, DoctorDashboard, ProviderDashboard, etc.) still use
 * the legacy UserRole and are unaffected by this addition.
 * ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ÃƒÂ¢Ã¢â‚¬ÂÃ¢â€šÂ¬ */

/** Coarse account split ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â matches backend AccountType exactly. */
export enum AccountType {
  ADMIN = 'ADMIN',
  CLIENT = 'CLIENT',
  PROVIDER = 'PROVIDER',
}

/**
 * Provider subtype (cadre) ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â matches backend enums/UserRole.java exactly.
 * Only meaningful when AccountType === PROVIDER.
 * NOTE: there is no backend equivalent for the frontend's old LEARNER
 * concept yet ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â Learner registration cannot be sent to /api/auth/register
 * until the backend adds a role for it (or it's handled as a separate flag).
 */
export enum ProviderRole {
  CLINIC = 'CLINIC',
  EDUCATOR = 'EDUCATOR',
  PHYSIOTHERAPY = 'PHYSIOTHERAPY',
  OCCUPATIONAL_THERAPY = 'OCCUPATIONAL_THERAPY',
  SPEECH_LANGUAGE_THERAPY = 'SPEECH_LANGUAGE_THERAPY',
  ORTHOTICS_PROSTHETICS = 'ORTHOTICS_PROSTHETICS',
  REHAB_PSYCHOLOGY = 'REHAB_PSYCHOLOGY',
  PHYSIATRY = 'PHYSIATRY',
  REHAB_NURSING = 'REHAB_NURSING',
  REFERRAL_GP = 'REFERRAL_GP',
  REFERRAL_CHW = 'REFERRAL_CHW',
}
